import React, { useEffect } from 'react';
import { VoiceMicIcon, VoiceMicOffIcon } from '@assets/icon';
import type { MicDialogType } from '../../../../hooks/useVoiceInteractionController';
import './MicPermissionDialog.scss';

interface DialogContent {
  variant: 'info' | 'error';
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel?: string;
}

const DIALOG_CONTENT: Record<MicDialogType, DialogContent> = {
  permission: {
    variant: 'info',
    title: '“Tendoo AI” muốn truy cập micrô',
    description: 'Sau khi cho phép, bạn chỉ cần trò chuyện với Tendoo AI để hoàn thành các việc',
    cancelLabel: 'Hủy bỏ',
    confirmLabel: 'Đồng ý',
  },
  'in-use': {
    variant: 'info',
    title: '“Tendoo AI” muốn truy cập micrô',
    description: 'Bạn đang trong cuộc gọi, không thể ghi âm trong ứng dụng',
    cancelLabel: 'Đã hiểu',
  },
  unsupported: {
    variant: 'error',
    title: 'Trình duyệt không hỗ trợ ghi âm',
    description:
      'Vui lòng mở Tendoo AI trên Chrome hoặc Safari phiên bản mới nhất để trò chuyện bằng giọng nói. Bạn vẫn có thể nhập tin nhắn để tiếp tục.',
    cancelLabel: 'Đã hiểu',
  },
  'not-found': {
    variant: 'error',
    title: 'Không tìm thấy micro',
    description: 'Vui lòng kết nối micro hoặc tai nghe có micro rồi thử lại. Bạn vẫn có thể nhập tin nhắn để tiếp tục.',
    cancelLabel: 'Đóng',
    confirmLabel: 'Thử lại',
  },
};

interface MicPermissionDialogProps {
  type: MicDialogType | null;
  onClose: () => void;
  onConfirm: () => void;
}

const MicPermissionDialog: React.FC<MicPermissionDialogProps> = ({ type, onClose, onConfirm }) => {
  useEffect(() => {
    if (!type) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [type, onClose]);

  if (!type) return null;
  const content = DIALOG_CONTENT[type];

  return (
    <div className="mic-dialog-overlay" onClick={onClose}>
      <div
        className="mic-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mic-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mic-dialog-header">
          <div className={`mic-dialog-icon mic-dialog-icon--${content.variant}`}>
            {content.variant === 'error' ? <VoiceMicOffIcon /> : <VoiceMicIcon />}
          </div>
          <div className="mic-dialog-text">
            <p id="mic-dialog-title" className="mic-dialog-title">{content.title}</p>
            <p className="mic-dialog-description">{content.description}</p>
          </div>
        </div>
        <div className="mic-dialog-actions">
          <button type="button" className="mic-dialog-btn mic-dialog-btn--outline" onClick={onClose}>
            {content.cancelLabel}
          </button>
          {content.confirmLabel && (
            <button type="button" className="mic-dialog-btn mic-dialog-btn--primary" onClick={onConfirm}>
              {content.confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MicPermissionDialog;
